import { Fragment, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import type { MessageDto, SessionDetailDto } from '../lib/api.ts';
import { useConfirm } from './ConfirmDialog.tsx';
import {
  Avatar,
  ChannelBadge,
  EmptyState,
  Spinner,
  formatDateHeading,
  formatTime,
} from './ui.tsx';
import {
  AlertTriangleIcon,
  ArchiveIcon,
  ChevronLeftIcon,
  MailIcon,
  MessageSquareIcon,
  PanelRightIcon,
  PhoneIcon,
} from './icons.tsx';

interface MessageThreadProps {
  session: SessionDetailDto;
  messages: MessageDto[];
  loading: boolean;
  currentUserId: number | null;
  onBack: () => void;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  onArchive: () => void;
  onSpam: () => void;
  onMarkUnread: () => void;
}

export function MessageThread({
  session,
  messages,
  loading,
  currentUserId,
  onBack,
  detailsOpen,
  onToggleDetails,
  onArchive,
  onSpam,
  onMarkUnread,
}: MessageThreadProps): React.JSX.Element {
  const confirm = useConfirm();
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottomRef = useRef(true);

  // Track whether the agent is reading history; if so, a new message must not
  // yank the viewport down.
  const handleScroll = (): void => {
    const element = scrollRef.current;
    if (!element) return;

    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    pinnedToBottomRef.current = distance < 120;
  };

  useEffect(() => {
    if (!pinnedToBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  // Switching conversations always starts at the newest message.
  useEffect(() => {
    pinnedToBottomRef.current = true;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [session.uuid]);

  return (
    <>
      <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <button type="button" className="lg-btn-ghost -ml-2 px-2 md:hidden" onClick={onBack}>
          <ChevronLeftIcon className="h-5 w-5" />
          <span className="sr-only">Back to conversations</span>
        </button>

        <button
          type="button"
          onClick={onToggleDetails}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <Avatar src={session.customerAvatar} name={session.customerName} size={38} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
              {session.customerName}
            </span>
            <span className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              {session.customerEmail ? (
                <span className="flex min-w-0 items-center gap-1">
                  <MailIcon className="h-3 w-3 shrink-0" />
                  <span className="truncate">{session.customerEmail}</span>
                </span>
              ) : null}
              {session.customerPhone ? (
                <span className="flex items-center gap-1">
                  <PhoneIcon className="h-3 w-3 shrink-0" />
                  {session.customerPhone}
                </span>
              ) : null}
            </span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1">
          <ChannelBadge type={session.type} />

          <button
            type="button"
            className="lg-btn-ghost px-2"
            title={session.archived ? 'Unarchive' : 'Archive'}
            onClick={onArchive}
          >
            <ArchiveIcon className="h-4 w-4" />
            <span className="sr-only">{session.archived ? 'Unarchive' : 'Archive'}</span>
          </button>

          <button
            type="button"
            className="lg-btn-ghost px-2"
            title="Mark as unread"
            onClick={onMarkUnread}
          >
            <MessageSquareIcon className="h-4 w-4" />
            <span className="sr-only">Mark as unread</span>
          </button>

          <button
            type="button"
            className="lg-btn-ghost px-2 hover:text-rose-600"
            title="Mark as spam"
            onClick={() => {
              void confirm({
                title: 'Mark as spam?',
                message:
                  "This blocks the customer's email and phone, and hides the conversation from every agent.",
                confirmLabel: 'Mark as spam',
                destructive: true,
              }).then((ok) => {
                if (ok) onSpam();
              });
            }}
          >
            <AlertTriangleIcon className="h-4 w-4" />
            <span className="sr-only">Mark as spam</span>
          </button>

          {/*
            The customer's name in this header also toggles the panel, but
            nothing marks it as clickable — this is the affordance people
            actually look for.
          */}
          <button
            type="button"
            aria-pressed={detailsOpen}
            className={clsx(
              'lg-btn-ghost px-2',
              detailsOpen && 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
            )}
            title={detailsOpen ? 'Hide details' : 'Show details'}
            onClick={onToggleDetails}
          >
            <PanelRightIcon className="h-4 w-4" />
            <span className="sr-only">{detailsOpen ? 'Hide details' : 'Show details'}</span>
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="lg-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50 px-4 py-4 dark:bg-slate-950"
      >
        {loading ? (
          <div className="flex justify-center py-10 text-brand-600">
            <Spinner className="h-5 w-5" />
          </div>
        ) : messages.length === 0 ? (
          <EmptyState fill title="No messages yet" description="Say hello to get things started." />
        ) : (
          <ol className="mx-auto max-w-3xl space-y-1">
            {messages.map((message, index) => {
              const previous = messages[index - 1];
              const showDate =
                !previous ||
                new Date(previous.createdAt).toDateString() !==
                  new Date(message.createdAt).toDateString();

              return (
                <Fragment key={message.id}>
                  {showDate ? (
                    <li className="flex justify-center py-3">
                      <span className="rounded-full bg-slate-200 px-3 py-1 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {formatDateHeading(message.createdAt)}
                      </span>
                    </li>
                  ) : null}

                  <MessageBubble
                    message={message}
                    currentUserId={currentUserId}
                    session={session}
                    grouped={
                      previous !== undefined &&
                      !showDate &&
                      previous.user.id === message.user.id &&
                      Boolean(previous.meta.system) === Boolean(message.meta.system)
                    }
                  />
                </Fragment>
              );
            })}
          </ol>
        )}

        <div ref={bottomRef} />
      </div>
    </>
  );
}

/**
 * Which route carried an outgoing message, ready to print.
 *
 * `meta.channel` is the answer whenever the server had one — it is set from the
 * delivery logs on a transcript and from the send's own outcome on the reply to
 * a send, which is what makes the label appear immediately rather than on the
 * next load. `type` is the fallback: the column was rewritten after a
 * successful send long before any of this existed, so old rows carry it there
 * and nowhere else.
 */
function deliveryChannel(message: MessageDto): string | null {
  const channel = message.meta.channel ?? (message.type === 'sms' || message.type === 'email' ? message.type : null);

  if (channel === null) return null;

  return channel === 'sms' ? 'SMS' : channel === 'fmp' ? 'Messenger' : 'email';
}

function MessageBubble({
  message,
  currentUserId,
  session,
  grouped,
}: {
  message: MessageDto;
  currentUserId: number | null;
  session: SessionDetailDto;
  grouped: boolean;
}): React.JSX.Element {
  const isSystem = Boolean(message.meta.system);
  const fromCustomer = message.user.id === null && !isSystem;
  const isMine = message.user.id !== null && message.user.id === currentUserId;

  const channel = deliveryChannel(message);

  // Delivery notices are for the agent only and read as neither side's message.
  if (message.meta.notice) {
    return (
      <li className="flex justify-center py-1">
        <span className="max-w-md rounded-lg bg-amber-100 px-3 py-1.5 text-center text-xs text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
          {message.content}
        </span>
      </li>
    );
  }

  // Attachments and text are separate fields, so a reply containing a comma is
  // no longer torn apart to find the image URLs.
  const images = message.attachments;
  const text = message.content;
  const actions = message.meta.actions ?? [];

  return (
    <li className={clsx('flex gap-2', fromCustomer ? 'justify-start' : 'justify-end')}>
      {fromCustomer ? (
        <span className={clsx('w-8 shrink-0', grouped && 'invisible')}>
          <Avatar src={session.customerAvatar} name={session.customerName} size={32} />
        </span>
      ) : null}

      <div
        className={clsx(
          'flex max-w-[min(36rem,80%)] flex-col',
          fromCustomer ? 'items-start' : 'items-end',
        )}
      >
        {!grouped && !fromCustomer && !isSystem ? (
          <span className="mb-0.5 px-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            {isMine ? 'You' : message.user.displayName}
          </span>
        ) : null}

        <div
          className={clsx(
            'rounded-2xl px-3.5 py-2 text-sm break-words whitespace-pre-wrap',
            isSystem
              ? 'bg-slate-200 text-slate-700 italic dark:bg-slate-800 dark:text-slate-300'
              : fromCustomer
                ? 'rounded-bl-md bg-white text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                : 'rounded-br-md bg-brand-600 text-white',
          )}
        >
          {text ? <p>{text}</p> : null}

          {images.length > 0 ? (
            <div
              className={clsx(
                'grid gap-1.5',
                images.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
                text && 'mt-2',
              )}
            >
              {images.map((attachment) => (
                <a
                  key={attachment.url}
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={attachment.name ?? 'Open full size'}
                >
                  <img
                    // The thumbnail keeps a transcript of photos from pulling
                    // several megabytes per message.
                    src={attachment.thumbnailUrl ?? attachment.url}
                    alt={attachment.name ?? 'Attachment'}
                    loading="lazy"
                    width={attachment.width}
                    height={attachment.height}
                    className="max-h-64 w-full rounded-lg object-cover"
                  />
                </a>
              ))}
            </div>
          ) : null}

          {actions.map((action) => (
            <a
              key={`${action.type}-${action.value}`}
              href={`tel:${action.value}`}
              className="mt-2 block rounded-lg bg-white/15 px-3 py-1.5 text-center text-xs font-medium hover:bg-white/25"
            >
              {action.label} {action.value}
            </a>
          ))}
        </div>

        <span className="mt-0.5 flex items-center gap-1.5 px-1 text-[11px] text-slate-400">
          {formatTime(message.createdAt)}
          {message.meta.status === 'failed' ? (
            // Which channel bounced, not just that something did: "not
            // delivered" alone leaves the agent to guess whether to try the
            // other one.
            <span className="text-rose-500" title="Delivery failed">
              · not delivered{channel ? ` by ${channel}` : ''}
            </span>
          ) : channel && !fromCustomer ? (
            <span title={`Delivered by ${channel}`}>· {channel}</span>
          ) : null}
        </span>
      </div>
    </li>
  );
}
